<script setup lang="ts">
import { ref } from 'vue';
import { MockSnsWebSdkInstance } from '@/integrations/Sumsub/MockSnsWebSdk';
import type { ApplicantStatusChangedPayload } from '@/integrations/Sumsub/types';
import MockRejectedScreen from './MockRejectedScreen.vue';
import MockLoadingState from './MockLoadingState.vue';
import MockVerificationButtons from './MockVerificationButtons.vue';
import MockClickCounter from './MockClickCounter.vue';

const props = defineProps({
  webSdkInstance: {
    type: MockSnsWebSdkInstance,
    required: true,
  },
});

// Centralized processing delay times
const PROCESSING_DELAYS = {
  SUCCESS: 3000,
  RESUBMISSION: 3000,
  REJECTED: 3000,
  ON_HOLD: 3000,
  PENDING: 3000,
} as const;

const isLoading = ref(false);
const clickCount = ref(0);
const lastClickType = ref<string>('');
const isRejected = ref(false);

// Generic handler function to reduce duplication
async function handleVerificationAction(options: {
  actionType: 'success' | 'resubmission' | 'rejected' | 'onHold' | 'pending';
  delay: number;
  payload: ApplicantStatusChangedPayload;
}) {
  clickCount.value++;
  lastClickType.value = options.actionType;
  isLoading.value = true;

  // Emit the status change immediately
  props.webSdkInstance?.emitApplicantStatusChanged(options.payload);

  // Check if this is a rejection and set the rejected state
  if (options.actionType === 'rejected') {
    isRejected.value = true;
  }

  // Simulate processing delay after emitting
  await new Promise((resolve) => setTimeout(resolve, options.delay));

  isLoading.value = false;
}

// Handler functions that call the generic handler
async function handleSuccess() {
  await handleVerificationAction({
    actionType: 'success',
    delay: PROCESSING_DELAYS.SUCCESS,
    payload: {
      levelName: 'fake_level',
      reprocessing: false,
      reviewStatus: 'completed',
      reviewResult: {
        reviewAnswer: 'GREEN',
      },
    },
  });
}

async function handleResubmission() {
  await handleVerificationAction({
    actionType: 'resubmission',
    delay: PROCESSING_DELAYS.RESUBMISSION,
    payload: {
      levelName: 'fake_level',
      reprocessing: false,
      reviewStatus: 'completed',
      reviewResult: {
        reviewAnswer: 'RED',
        reviewRejectType: 'RETRY',
      },
    },
  });
}

async function handleRejected() {
  await handleVerificationAction({
    actionType: 'rejected',
    delay: PROCESSING_DELAYS.REJECTED,
    payload: {
      levelName: 'fake_level',
      reprocessing: false,
      reviewStatus: 'completed',
      reviewResult: {
        reviewAnswer: 'RED',
        reviewRejectType: 'FINAL',
      },
    },
  });
}

async function handlePending() {
  await handleVerificationAction({
    actionType: 'pending',
    delay: PROCESSING_DELAYS.PENDING,
    payload: {
      levelName: 'fake_level',
      reprocessing: false,
      reviewStatus: 'pending',
    },
  });
}

async function handleOnHold() {
  await handleVerificationAction({
    actionType: 'onHold',
    delay: PROCESSING_DELAYS.ON_HOLD,
    payload: {
      levelName: 'fake_level',
      reprocessing: false,
      reviewStatus: 'onHold',
    },
  });
}
</script>

<template>
  <div
    class="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
    data-test="mock-sumsub-container"
  >
    <div class="mb-6 flex items-start justify-between">
      <div>
        <h3 class="mb-2 text-lg font-semibold text-gray-900">Mock Identity Verification</h3>
        <p class="text-sm text-gray-600">simulate different verification outcomes</p>
      </div>

      <MockClickCounter :click-count="clickCount" :last-click-type="lastClickType" />
    </div>

    <!-- Rejected Screen -->
    <MockRejectedScreen v-if="isRejected && !isLoading" />

    <!-- Loading State -->
    <MockLoadingState v-else-if="isLoading" :action-type="lastClickType" />

    <!-- Buttons -->
    <MockVerificationButtons
      v-else
      :is-loading="isLoading"
      @success="handleSuccess"
      @resubmission="handleResubmission"
      @rejected="handleRejected"
      @on-hold="handleOnHold"
      @pending="handlePending"
    />
  </div>
</template>
